import { ErrorActorEvent, NonReducibleUnknown, sendParent } from 'xstate';
import { ActionEventParams, ActorErrorParams } from '../../../../lib/types';
import { SendChildMachineReady } from '../../types/events';
import { FraudAlloyContext } from './fraudAlloyMachine';
import {
  InitializingFraudAlloyResult,
  PollIDUploadJourneyStatusKYCResult,
  PollIDUploadJourneyStatusResult,
} from './types/actors';
import {
  FraudAlloyMachineEvents,
  SendIDUploadJourneyFailure,
  SendIDUploadJourneyPending,
  SendIDUploadJourneySuccess,
  SendInitializeFraudAlloyActorDone,
  SendPollIDUploadJourneyStatusActorDone,
  SendPollIDUploadJourneyStatusKYCActorDone,
} from './types/events';

/**
 * Gets InitializingFraudAlloyResult from initializeFraudAlloy SendInitializeFraudAlloyActorDone
 * @param ActionEventParams<FraudAlloyContext, SendInitializeFraudAlloyActorDone>
 * @returns InitializingFraudAlloyResult
 */
export const getInitializeFraudAlloyParams = ({
  event,
}: ActionEventParams<
  FraudAlloyContext,
  SendInitializeFraudAlloyActorDone
>): InitializingFraudAlloyResult => {
  return event.output;
};

/**
 * Gets PollIDUploadJourneyStatusResult from pollIDUploadJourneyStatus
 * @param ActionEventParams<FraudAlloyContext, SendPollIDUploadJourneyStatusActorDone>
 * @returns PollIDUploadJourneyStatusResult
 */
export const getPollIDUploadJourneyStatusParams = ({
  event,
}: ActionEventParams<
  FraudAlloyContext,
  SendPollIDUploadJourneyStatusActorDone
>): PollIDUploadJourneyStatusResult => {
  return event.output;
};

/**
 * Gets PollIDUploadJourneyStatusKYCResult from pollIDUploadJourneyStatusKYC
 * @param ActionEventParams<FraudAlloyContext, SendPollIDUploadJourneyStatusKYCActorDone>
 * @returns PollIDUploadJourneyStatusKYCResult
 */
export const getPollIDUploadJourneyStatusKYCParams = ({
  event,
}: ActionEventParams<
  FraudAlloyContext,
  SendPollIDUploadJourneyStatusKYCActorDone
>): PollIDUploadJourneyStatusKYCResult => {
  return event.output;
};

/**
 * Gets ActorErrorParams from error event
 * @param ActionEventParams<FraudAlloyMachineEvents, ErrorActorEvent>
 * @returns ActorErrorParams
 */
export const getServiceErrorParams = ({
  event,
}: ActionEventParams<FraudAlloyContext, ErrorActorEvent>): ActorErrorParams => {
  const errorOutput = event.error as ActorErrorParams;

  return {
    type: event.type,
    error: errorOutput,
    reject: errorOutput.reject,
  };
};

/**
 * Sends IDUploadJourney success event to parent
   @returns SendCreateVirtualCardIssuanceSuccess
 */
export const emitIDUploadJourneySuccess = sendParent<
  FraudAlloyContext,
  FraudAlloyMachineEvents,
  NonReducibleUnknown,
  SendIDUploadJourneySuccess,
  FraudAlloyMachineEvents,
  never,
  never
>(() => ({
  type: 'SEND_ID_UPLOAD_JOURNEY_SUCCESS',
}));

/**
 * Sends IDUploadJourney success event to parent
   @returns SendCreateVirtualCardIssuanceSuccess
 */
export const emitIDUploadJourneyFailure = sendParent<
  FraudAlloyContext,
  FraudAlloyMachineEvents,
  NonReducibleUnknown,
  SendIDUploadJourneyFailure,
  FraudAlloyMachineEvents,
  never,
  never
>(() => ({
  type: 'SEND_ID_UPLOAD_JOURNEY_FAILURE',
}));

/**
 * Sends IDUploadJourney pending event to parent
   @returns SendIDUploadJourneyPending
 */
export const emitIDUploadJourneyPending = sendParent<
  FraudAlloyContext,
  FraudAlloyMachineEvents,
  NonReducibleUnknown,
  SendIDUploadJourneyPending,
  FraudAlloyMachineEvents,
  never,
  never
>(() => ({
  type: 'SEND_ID_UPLOAD_JOURNEY_PENDING',
}));

/**
 * Sends create virtual card issuance attempting event to parent
   @returns SendCreateVirtualCardIssuanceAttempting
 */
export const emitFraudAlloyMachineReady = sendParent<
  FraudAlloyContext,
  FraudAlloyMachineEvents,
  NonReducibleUnknown,
  SendChildMachineReady,
  FraudAlloyMachineEvents,
  never,
  never
>(() => {
  return {
    type: 'SEND_CHILD_MACHINE_READY',
    params: {
      machineId: 'fraudAlloyMachine',
    },
  };
});
